import dayjs from 'dayjs'
import type { ConsolOutboundFilter } from '@/services/global/consolOutbound/types'

/**
 * 메뉴 세션·URL 쿼리에 싣는 조회 조건 모양 (MOMS 공통 UX — 다른 메뉴를 다녀와도 마지막 조회가 남는다).
 *
 * 조회 파라미터(ConsolOutboundFilter)가 이미 문자열·문자열배열뿐이라 그대로 싣는다.
 * 목록 검색어와 페이지 번호도 함께 싣는다 — 조건만 살아나고 보던 페이지가 1로 돌아가면
 * 사람은 여전히 새로고침됐다고 느낀다.
 *
 * 표기는 `Utils.qsStringify`(arrayFormat: 'comma') ↔ `useParsedQuery`(qs.parse comma:true) 왕복을 전제한다.
 * 원소가 하나인 배열은 쉼표가 없어 문자열로 되돌아오므로 복원에서 배열로 되돌린다.
 *
 * 9개 필드를 모두 싣는다. FilterArea 안에서는 `form.setFieldValue` 로 심은 값이 입력에 그대로
 * 표시되는 것을 pbo-core 0.7.4 실측으로 확인했다 — 날짜(시작일·종료일 input), 다건 코드입력
 * (`mfs.FormTextArea`, "A1, A2" 로 표시), 단일 텍스트, 셀렉트 전부 해당한다. 표시가 안 되는 필드가
 * 있으면 조건은 좁은데 화면은 비어 보이므로 그 필드는 저장 대상에서 빼야 한다.
 */
export interface ConsolOutboundSearchQuery {
  shipperCode?: string
  orderDateFrom?: string
  orderDateTo?: string
  outboundPlannedDateFrom?: string
  outboundPlannedDateTo?: string
  sourceOrderNumbers?: string[]
  inboundInvoiceNumber?: string
  globalInvoiceNumbers?: string[]
  orderNumbers?: string[]
  serviceType?: string
  orderStatus?: string
  listQuery?: string
  page?: string
}

/** 화면이 복원해야 하는 상태 — 조회 조건 + 표 보기 상태. */
export interface ConsolOutboundViewState {
  filter: ConsolOutboundFilter | null
  listQuery: string
  page: number
}

/**
 * 단일 문자열 값 — 쉼표가 들어 있으면 배열로 쪼개져 돌아오므로 도로 붙인다.
 *
 * `Utils.qsStringify` 는 `encode: false` 라 값 안의 쉼표를 날것 그대로 URL 에 싣고,
 * `useParsedQuery` 의 `qs.parse(comma: true)` 가 그 쉼표를 배열 구분자로 읽는다.
 * 배열을 그냥 버리면 `송장번호(입고)` 에 "48211003920201, 48211003920202" 를 넣어 조회한 조건이
 * 복원에서 통째로 사라진다 — 조건은 좁은데 입력은 비어 보인다. 쪼갠 자리에 그대로 다시 붙인다
 * (qs 는 쪼갤 때 공백을 다듬지 않으므로 원문이 그대로 돌아온다).
 */
const text = (v: unknown): string | undefined => {
  if (Array.isArray(v)) return v.every((x) => typeof x === 'string') ? text(v.join(',')) : undefined
  return typeof v === 'string' && v.trim() ? v.trim() : undefined
}

/** 쉼표 배열은 원소가 둘 이상일 때만 배열로 돌아온다 — 문자열 하나도 목록으로 받는다. */
const list = (v: unknown): string[] | undefined => {
  if (Array.isArray(v)) {
    const items = v.map((x) => text(x)).filter((x): x is string => !!x)
    return items.length ? items : undefined
  }
  const one = text(v)
  return one ? [one] : undefined
}

/**
 * 실재하는 yyyyMMdd 만 통과시킨다 — 손상되거나 공유된 URL 이 폼을 깨지 않게 한다.
 * 자릿수만 보면 20261345 같은 값이 통과하므로 되돌린 값이 원문과 같은지까지 확인한다
 * (dayjs 커스텀 포맷 플러그인을 앱이 등록하지 않아 ISO 로 바꿔 파싱한다 — stockLedger 선례).
 */
const ymd = (v: unknown): string | undefined => {
  const s = text(v)
  if (!s || !/^\d{8}$/.test(s)) return undefined
  const parsed = dayjs(`${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`)
  return parsed.isValid() && parsed.format('YYYYMMDD') === s ? s : undefined
}

/** 1 이상 정수만 통과시킨다. 그 외에는 첫 페이지로 연다. */
const pageNo = (v: unknown): number => {
  const n = Number(text(v))
  return Number.isInteger(n) && n >= 1 ? n : 1
}

/** 세션·URL 에 저장할 형태로 만든다. 빈 값은 키 자체를 빼 쿼리스트링을 짧게 유지한다. */
export const toSearchQuery = (filter: ConsolOutboundFilter, view?: { listQuery?: string; page?: number }): ConsolOutboundSearchQuery => ({
  ...(filter.shipperCode ? { shipperCode: filter.shipperCode } : {}),
  ...(filter.orderDateFrom ? { orderDateFrom: filter.orderDateFrom } : {}),
  ...(filter.orderDateTo ? { orderDateTo: filter.orderDateTo } : {}),
  ...(filter.outboundPlannedDateFrom ? { outboundPlannedDateFrom: filter.outboundPlannedDateFrom } : {}),
  ...(filter.outboundPlannedDateTo ? { outboundPlannedDateTo: filter.outboundPlannedDateTo } : {}),
  ...(filter.sourceOrderNumbers?.length ? { sourceOrderNumbers: filter.sourceOrderNumbers } : {}),
  ...(filter.inboundInvoiceNumber ? { inboundInvoiceNumber: filter.inboundInvoiceNumber } : {}),
  ...(filter.globalInvoiceNumbers?.length ? { globalInvoiceNumbers: filter.globalInvoiceNumbers } : {}),
  ...(filter.orderNumbers?.length ? { orderNumbers: filter.orderNumbers } : {}),
  ...(filter.serviceType ? { serviceType: filter.serviceType } : {}),
  ...(filter.orderStatus ? { orderStatus: filter.orderStatus } : {}),
  ...(view?.listQuery ? { listQuery: view.listQuery } : {}),
  ...(view?.page && view.page > 1 ? { page: String(view.page) } : {}),
})

/**
 * 저장된 쿼리를 화면 상태로 되돌린다.
 *
 * 화주와 주문일자 양끝이 다 있어야 조회가 성립한다(BE 필수 · hasOrderDateRange 가드). 하나라도 없으면
 * 복원할 조건이 없는 것으로 보고 filter 를 null 로 준다 — 화면은 기본값(오늘)으로 열린다.
 */
export const fromSearchQuery = (query: Partial<ConsolOutboundSearchQuery>): ConsolOutboundViewState => {
  const shipperCode = text(query.shipperCode)
  const orderDateFrom = ymd(query.orderDateFrom)
  const orderDateTo = ymd(query.orderDateTo)

  const filter =
    shipperCode && orderDateFrom && orderDateTo
      ? {
          shipperCode,
          orderDateFrom,
          orderDateTo,
          outboundPlannedDateFrom: ymd(query.outboundPlannedDateFrom),
          outboundPlannedDateTo: ymd(query.outboundPlannedDateTo),
          sourceOrderNumbers: list(query.sourceOrderNumbers),
          inboundInvoiceNumber: text(query.inboundInvoiceNumber),
          globalInvoiceNumbers: list(query.globalInvoiceNumbers),
          orderNumbers: list(query.orderNumbers),
          serviceType: text(query.serviceType),
          orderStatus: text(query.orderStatus),
        }
      : null

  return { filter, listQuery: filter ? (text(query.listQuery) ?? '') : '', page: filter ? pageNo(query.page) : 1 }
}
